import React, { useMemo, memo, useRef } from 'react';
import { calculateSmartTicks } from '../../utils/scaleHelper';
import { filterTicksWithinBounds } from '../../utils/boundsHelper';
import { DynamicScaleLabel } from './DynamicScaleLabel';
import { appProfiler } from '../../utils/appProfiler';

export const DynamicPriceAxis = memo(({ 
  min = 64000, 
  max = 65000, 
  mapValue, 
  bounds, // [minY, maxY] e.g. [bottomOfGrid, topOfGrid]
  padding = 0,
  anchorX = "right", 
  anchorY = "middle",
  targetTicks = 8,
  ...props 
}: any) => {
  // Deadzone threshold (0.5% buffer) + 1.5s throttle
  const lastBoundsRef = useRef<{ min: number; max: number; time: number }>({ min, max, time: 0 });
  const effBoundsRef = useRef<{ min: number; max: number }>({ min, max });

  const now = Date.now();
  const range = Math.max(max - min, 0.0001);
  const minDiff = Math.abs(min - lastBoundsRef.current.min) / range;
  const maxDiff = Math.abs(max - lastBoundsRef.current.max) / range;
  const timeDiff = now - lastBoundsRef.current.time;

  if (minDiff > 0.005 || maxDiff > 0.005 || timeDiff > 1500 || effBoundsRef.current.min === undefined) {
    lastBoundsRef.current = { min, max, time: now };
    effBoundsRef.current = { min, max };
  }

  const effMin = effBoundsRef.current.min;
  const effMax = effBoundsRef.current.max;

  const ticks = useMemo(() => appProfiler.time('Axes:calculateSmartTicks:Price', () => calculateSmartTicks(effMin, effMax, 'price', targetTicks)), [effMin, effMax, targetTicks]);

  const primary = useMemo(() => filterTicksWithinBounds(ticks.primary, mapValue, bounds, padding), [ticks.primary, mapValue, bounds, padding]);
  const secondary = useMemo(() => filterTicksWithinBounds(ticks.secondary, mapValue, bounds, padding), [ticks.secondary, mapValue, bounds, padding]);
  
  return (
    <group {...props}>
      {primary.map((t, idx) => (
        <DynamicScaleLabel key={`p1-${idx}-${t.value}-${t.label}`} text={t.label} x={0} y={mapValue ? mapValue(t.value) : t.value} z={0} lodLevel={1} anchorX={anchorX} anchorY={anchorY} />
      ))}
      {secondary.map((t, idx) => (
        <DynamicScaleLabel key={`p2-${idx}-${t.value}-${t.label}`} text={t.label} x={0} y={mapValue ? mapValue(t.value) : t.value} z={0} lodLevel={2} anchorX={anchorX} anchorY={anchorY} />
      ))}
    </group>
  );
});

export const DynamicTimeAxis = memo(({ 
  min = Date.now() - 24*3600*1000, 
  max = Date.now(), 
  mapValue, 
  bounds,
  padding = 0,
  anchorX = "center", 
  anchorY = "top", 
  axis = "x",
  targetTicks = 8,
  ...props 
}: any) => {
  // Deadzone threshold (0.5% buffer) + 2.0s throttle
  const lastBoundsRef = useRef<{ min: number; max: number; time: number }>({ min, max, time: 0 });
  const effBoundsRef = useRef<{ min: number; max: number }>({ min, max });

  const now = Date.now();
  const range = Math.max(max - min, 1000);
  const minDiff = Math.abs(min - lastBoundsRef.current.min) / range;
  const maxDiff = Math.abs(max - lastBoundsRef.current.max) / range;
  const timeDiff = now - lastBoundsRef.current.time;

  if (minDiff > 0.005 || maxDiff > 0.005 || timeDiff > 2000 || effBoundsRef.current.min === undefined) {
    lastBoundsRef.current = { min, max, time: now };
    effBoundsRef.current = { min, max };
  }

  const effMin = effBoundsRef.current.min;
  const effMax = effBoundsRef.current.max;

  const ticks = useMemo(() => appProfiler.time('Axes:calculateSmartTicks:Time', () => calculateSmartTicks(effMin, effMax, 'time', targetTicks)), [effMin, effMax, targetTicks]);

  const filterCollisions = (tickList: any[], lodLevel: number, minPadding: number = 8) => {
    if (!tickList || tickList.length === 0) return [];
    
    const withinBounds = filterTicksWithinBounds(tickList, mapValue, bounds, padding);
    const result: any[] = [];
    let lastEnd = -Infinity;
    
    const sorted = [...withinBounds].sort((a, b) => {
      const posA = mapValue ? mapValue(a.value) : a.value;
      const posB = mapValue ? mapValue(b.value) : b.value;
      return posA - posB;
    });

    for (const t of sorted) {
      const pos = mapValue ? mapValue(t.value) : t.value;
      const baseFontSize = 18; 
      const scaleMultiplier = lodLevel === 1 ? 1.0 : 0.75;
      const estimatedWidth = t.label.length * (0.6 * baseFontSize * scaleMultiplier);
      
      const start = pos - estimatedWidth / 2;
      const end = pos + estimatedWidth / 2;
      
      if (start >= lastEnd + minPadding) {
        result.push(t);
        lastEnd = end;
      }
    }
    return result;
  };

  const primary = useMemo(() => filterCollisions(ticks.primary, 1), [ticks.primary, mapValue, bounds, padding]);

  return (
    <group {...props}>
      {primary.map((t, idx) => {
        const val = mapValue ? mapValue(t.value) : t.value;
        const posX = axis === 'x' ? val : 0;
        const posY = axis === 'y' ? val : 0;
        const posZ = axis === 'z' ? val : 0;
        return (
          <DynamicScaleLabel key={`t1-${idx}-${t.value}-${t.label}`} text={t.label} x={posX} y={posY} z={posZ} lodLevel={1} anchorX={anchorX} anchorY={anchorY} />
        );
      })}
    </group>
  );
});

export const DynamicForceAxis = memo(({ 
  min = 0, 
  max = 10000000, 
  mapValue, 
  bounds,
  padding = 0,
  anchorX = "left", 
  anchorY = "middle", 
  axis = "x",
  targetTicks = 8,
  colorUp,
  colorDown,
  color = '#ffffff',
  ...props 
}: any) => {
  // Deadzone threshold (0.5% buffer) + 1.5s throttle
  const lastBoundsRef = useRef<{ min: number; max: number; time: number }>({ min, max, time: 0 });
  const effBoundsRef = useRef<{ min: number; max: number }>({ min, max });

  const now = Date.now();
  const range = Math.max(max - min, 1);
  const minDiff = Math.abs(min - lastBoundsRef.current.min) / range;
  const maxDiff = Math.abs(max - lastBoundsRef.current.max) / range;
  const timeDiff = now - lastBoundsRef.current.time;

  if (minDiff > 0.005 || maxDiff > 0.005 || timeDiff > 1500 || effBoundsRef.current.min === undefined) {
    lastBoundsRef.current = { min, max, time: now };
    effBoundsRef.current = { min, max };
  }

  const effMin = effBoundsRef.current.min;
  const effMax = effBoundsRef.current.max;

  const ticks = useMemo(() => appProfiler.time('Axes:calculateSmartTicks:Force', () => calculateSmartTicks(effMin, effMax, 'force', targetTicks)), [effMin, effMax, targetTicks]);

  const primary = useMemo(() => filterTicksWithinBounds(ticks.primary, mapValue, bounds, padding), [ticks.primary, mapValue, bounds, padding]);

  const getColor = (val: number) => {
    if (val > 0 && colorUp) return colorUp;
    if (val < 0 && colorDown) return colorDown;
    return color;
  };

  return (
    <group {...props}>
      {primary.map((t, idx) => {
        const val = mapValue ? mapValue(t.value) : t.value;
        const posX = axis === 'x' ? val : 0;
        const posY = axis === 'y' ? val : 0;
        const posZ = axis === 'z' ? val : 0;
        return (
          <DynamicScaleLabel key={`f1-${idx}-${t.value}-${t.label}`} text={t.label} x={posX} y={posY} z={posZ} lodLevel={1} anchorX={anchorX} anchorY={anchorY} color={getColor(t.value)} />
        );
      })}
    </group>
  );
});

